import { useState, useEffect } from 'react';
import { useExperiment } from 'statsig-react';

type useExperimentCustomProps<T> = {
  experiment: string;
  default?: T;
  getter: string;
};

export type ExperimentStatus<T> = {
  value: T;
  isLoading: boolean;
};

type useExperimentResult<T> = {
  [key: string]: ExperimentStatus<T>;
};

const _queryParamOverride = (e: ExperimentStatus<any>, experimentName: string) => {
  if (typeof window === 'undefined') {
    return e; // No window, do nothing
  }
  const query = window.location.search
    .replace('?', '')
    .split('&')
    .reduce(
      (p, c) => {
        const [key, value] = c.split('=');
        p[key] = value;
        return p;
      },
      {} as { [key: string]: string }
    );
  if (query[experimentName]) {
    e.value = query[experimentName] === 'true' ? true : false;
  }
  return e;
};

const useExperimentCustom = <T>(configs: useExperimentCustomProps<T>[]): useExperimentResult<T> => {
  return configs.reduce((acc, config) => {
    try {
      const [timeoutId, setTimeoutId] = useState<null | NodeJS.Timeout>(null);
      const { isLoading, config: providerConfig } = useExperiment(config.experiment);
      const getterValue = providerConfig.get(config.getter, null);
      const [experimentStatus, setExperimentStatus] = useState<ExperimentStatus<T>>({
        value: null,
        isLoading: true,
      });

      useEffect(() => {
        if (!isLoading && getterValue === null && !timeoutId) {
          const timer = setTimeout(() => {
            setTimeoutId(null);
            setExperimentStatus({
              ...experimentStatus,
              isLoading: false,
            });
          }, 800);

          setExperimentStatus({
            ...experimentStatus,
            isLoading: true,
          });
          setTimeoutId(timer);
        } else if (!isLoading && getterValue && timeoutId) {
          clearTimeout(timeoutId);
          setTimeoutId(null);
        }
      }, [isLoading, getterValue]);

      useEffect(() => {
        if (!isLoading && getterValue === null && !timeoutId) return;

        setExperimentStatus({
          value: getterValue as T,
          isLoading: timeoutId ? true : isLoading,
        });
      }, [isLoading, getterValue, timeoutId]);

      acc[config.getter] = _queryParamOverride(experimentStatus, config.experiment);

      if (typeof window !== 'undefined') {
        (window as any).TrackJS &&
          (window as any).TrackJS.addMetadata('statsig_ruleId', providerConfig.getRuleID() || '');
      }

      return acc;
    } catch (e: any) {
      if (typeof window !== 'undefined') {
        (window as any).TrackJS && (window as any).TrackJS.track(`<Statsig Hook error> ${e}`);
      }
      acc[config.getter] = { isLoading: false, value: null };
      return acc;
    }
  }, {} as useExperimentResult<T>);
};

export default useExperimentCustom;
